import { useEffect, useState, useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  SettingsTabs,
  SettingsTabContent,
  SettingsFormSection,
  SettingsField,
  SettingsToggleField,
  ToggleSwitch,
  RAGThresholdVisualizer,
} from '@/components/settings';
interface NotificationSettings {
  emailEnabled: boolean;
  dashboardAlertsEnabled: boolean;
  ragChangeNotifications: boolean;
  dailyDigest: boolean;
  dailyDigestTime: string;
}

interface OrganizationSettingsData {
  id: string;
  organizationId: string;
  defaultTargetMarginPercent: number;
  spokeDeliveryFrequencyDays: number;
  ragGreenThreshold: number;
  ragAmberThreshold: number;
  lowStockWarningDays: number;
  criticalStockDays: number;
  expiringBatchAlertDays: number;
  downtimeAlertHours: number;
  currency: 'GBP' | 'EUR' | 'USD';
  timezone: string;
  dateFormat: 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD';
  notifications: NotificationSettings;
}

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'regional', label: 'Regional' },
  { id: 'business', label: 'Business Defaults' },
  { id: 'rag', label: 'RAG Thresholds' },
  { id: 'alerts', label: 'Alerts' },
];

const TIMEZONES = [
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Europe/Rome',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
];

export default function OrganizationSettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState<OrganizationSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch('/api/v1/settings');
      if (res.status === 401) {
        navigate('/');
        return;
      }
      if (res.status === 403) {
        setError('You do not have permission to view settings');
        return;
      }
      if (!res.ok) {
        throw new Error('Failed to load settings');
      }
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const validateSettings = (data: Partial<OrganizationSettingsData>): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (data.ragAmberThreshold !== undefined && data.ragGreenThreshold !== undefined) {
      if (data.ragAmberThreshold >= data.ragGreenThreshold) {
        errors.ragAmberThreshold = 'Amber threshold must be less than Green threshold';
      }
    }
    if (data.criticalStockDays !== undefined && data.lowStockWarningDays !== undefined) {
      if (data.criticalStockDays >= data.lowStockWarningDays) {
        errors.criticalStockDays = 'Critical stock days must be less than warning days';
      }
    }
    return errors;
  };

  const updateSettings = async (updates: Partial<OrganizationSettingsData>) => {
    if (!settings) return;

    const merged = { ...settings, ...updates };
    const errors = validateSettings(merged);
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setSaving(true);
      setError(null);
      const res = await apiFetch('/api/v1/settings', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to save settings');
      }
      const data = await res.json();
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleFieldChange = <K extends keyof OrganizationSettingsData>(
    field: K,
    value: OrganizationSettingsData[K]
  ) => {
    if (settings) {
      setSettings({ ...settings, [field]: value });
    }
  };

  const handleNotificationChange = <K extends keyof NotificationSettings>(
    field: K,
    value: NotificationSettings[K]
  ) => {
    if (settings) {
      const updatedNotifications = { ...settings.notifications, [field]: value };
      setSettings({ ...settings, notifications: updatedNotifications });
    }
  };

  const handleSave = () => {
    if (settings) {
      updateSettings(settings);
    }
  };

  const handleResetToDefaults = async () => {
    try {
      setSaving(true);
      setError(null);
      const res = await apiFetch('/api/v1/settings/reset', {
        method: 'POST',
      });
      if (!res.ok) {
        throw new Error('Failed to reset settings');
      }
      const data = await res.json();
      setSettings(data);
      setValidationErrors({});
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen">
        <div className="space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-muted rounded" />
            <div className="h-64 bg-muted rounded" />
          </div>
        </div>
      </div>
    );
  }

  if (error && !settings) {
    return (
      <div className="min-h-screen">
        <div className="space-y-6">
          <Card>
            <CardContent className="py-8">
              <p className="text-center text-destructive">{error}</p>
              <div className="mt-4 flex justify-center">
                <Button onClick={loadSettings}>Retry</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (!settings) return null;

  return (
    <div className="min-h-screen">
      <div className="space-y-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Organization Settings</h1>
            <p className="text-sm text-muted-foreground">
              Manage your organization's configuration and preferences
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleResetToDefaults} disabled={saving}>
              Reset to Defaults
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <Card>
          <CardHeader className="pb-0">
            <SettingsTabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />
          </CardHeader>
          <CardContent>
            {activeTab === 'general' && (
              <SettingsTabContent>
                <SettingsFormSection
                  title="Organization Information"
                  description="Basic information about your organization"
                >
                  <SettingsField label="Organization ID" htmlFor="orgId">
                    <Input
                      id="orgId"
                      value={settings.organizationId}
                      disabled
                      className="bg-muted"
                    />
                  </SettingsField>
                </SettingsFormSection>
              </SettingsTabContent>
            )}

            {activeTab === 'regional' && (
              <SettingsTabContent>
                <SettingsFormSection
                  title="Regional Settings"
                  description="Configure currency, timezone, and date format preferences"
                >
                  <SettingsField label="Currency" htmlFor="currency">
                    <Select
                      value={settings.currency}
                      onValueChange={(value) =>
                        handleFieldChange('currency', value as 'GBP' | 'EUR' | 'USD')
                      }
                    >
                      <SelectTrigger id="currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GBP">GBP - British Pound</SelectItem>
                        <SelectItem value="EUR">EUR - Euro</SelectItem>
                        <SelectItem value="USD">USD - US Dollar</SelectItem>
                      </SelectContent>
                    </Select>
                  </SettingsField>

                  <SettingsField label="Timezone" htmlFor="timezone">
                    <Select
                      value={settings.timezone}
                      onValueChange={(value) => handleFieldChange('timezone', value)}
                    >
                      <SelectTrigger id="timezone">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz} value={tz}>
                            {tz}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </SettingsField>

                  <SettingsField label="Date Format" htmlFor="dateFormat">
                    <Select
                      value={settings.dateFormat}
                      onValueChange={(value) =>
                        handleFieldChange('dateFormat', value as 'DD/MM/YYYY' | 'MM/DD/YYYY' | 'YYYY-MM-DD')
                      }
                    >
                      <SelectTrigger id="dateFormat">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                        <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                        <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                      </SelectContent>
                    </Select>
                  </SettingsField>
                </SettingsFormSection>
              </SettingsTabContent>
            )}

            {activeTab === 'business' && (
              <SettingsTabContent>
                <SettingsFormSection
                  title="Business Defaults"
                  description="Default values for business operations"
                >
                  <SettingsField
                    label="Default Target Margin (%)"
                    htmlFor="targetMargin"
                    description="0-100%"
                  >
                    <Input
                      id="targetMargin"
                      type="number"
                      min={0}
                      max={100}
                      value={settings.defaultTargetMarginPercent}
                      onChange={(e) =>
                        handleFieldChange('defaultTargetMarginPercent', parseInt(e.target.value, 10) || 0)
                      }
                    />
                  </SettingsField>

                  <SettingsField
                    label="Spoke Delivery Frequency (days)"
                    htmlFor="spokeDelivery"
                    description="1-30 days"
                  >
                    <Input
                      id="spokeDelivery"
                      type="number"
                      min={1}
                      max={30}
                      value={settings.spokeDeliveryFrequencyDays}
                      onChange={(e) =>
                        handleFieldChange('spokeDeliveryFrequencyDays', parseInt(e.target.value, 10) || 1)
                      }
                    />
                  </SettingsField>
                </SettingsFormSection>
              </SettingsTabContent>
            )}

            {activeTab === 'rag' && (
              <SettingsTabContent>
                <SettingsFormSection
                  title="RAG Threshold Configuration"
                  description="Configure the thresholds for Red, Amber, and Green status indicators"
                >
                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-base">Stock Level Thresholds</CardTitle>
                      <CardDescription>
                        Define the percentage thresholds for stock level indicators
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <SettingsField
                          label="GREEN: Stock at or above"
                          htmlFor="ragGreen"
                          description="% of capacity"
                          error={validationErrors.ragGreenThreshold}
                        >
                          <Input
                            id="ragGreen"
                            type="number"
                            min={1}
                            max={100}
                            value={settings.ragGreenThreshold}
                            onChange={(e) =>
                              handleFieldChange('ragGreenThreshold', parseInt(e.target.value, 10) || 80)
                            }
                          />
                        </SettingsField>

                        <SettingsField
                          label="AMBER: Stock between threshold and GREEN"
                          htmlFor="ragAmber"
                          description="% of capacity"
                          error={validationErrors.ragAmberThreshold}
                        >
                          <Input
                            id="ragAmber"
                            type="number"
                            min={0}
                            max={100}
                            value={settings.ragAmberThreshold}
                            onChange={(e) =>
                              handleFieldChange('ragAmberThreshold', parseInt(e.target.value, 10) || 60)
                            }
                          />
                        </SettingsField>
                      </div>

                      <div className="pt-4">
                        <Label className="text-sm font-medium mb-2 block">Threshold Preview</Label>
                        <RAGThresholdVisualizer
                          greenThreshold={settings.ragGreenThreshold}
                          amberThreshold={settings.ragAmberThreshold}
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="bg-muted/50">
                    <CardHeader>
                      <CardTitle className="text-base">Stock Alert Thresholds</CardTitle>
                      <CardDescription>
                        Configure when stock alerts should be triggered
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <SettingsField
                        label="Low stock warning"
                        htmlFor="lowStock"
                        description="Alert when days of cover ≤ this value"
                        error={validationErrors.lowStockWarningDays}
                      >
                        <div className="flex items-center gap-2">
                          <Input
                            id="lowStock"
                            type="number"
                            min={1}
                            max={90}
                            value={settings.lowStockWarningDays}
                            onChange={(e) =>
                              handleFieldChange('lowStockWarningDays', parseInt(e.target.value, 10) || 7)
                            }
                            className="w-24"
                          />
                          <span className="text-sm text-muted-foreground">days</span>
                        </div>
                      </SettingsField>

                      <SettingsField
                        label="Critical stock"
                        htmlFor="criticalStock"
                        description="Alert when days of cover ≤ this value"
                        error={validationErrors.criticalStockDays}
                      >
                        <div className="flex items-center gap-2">
                          <Input
                            id="criticalStock"
                            type="number"
                            min={1}
                            max={30}
                            value={settings.criticalStockDays}
                            onChange={(e) =>
                              handleFieldChange('criticalStockDays', parseInt(e.target.value, 10) || 3)
                            }
                            className="w-24"
                          />
                          <span className="text-sm text-muted-foreground">days</span>
                        </div>
                      </SettingsField>

                      <SettingsField
                        label="Expiring batches"
                        htmlFor="expiringBatch"
                        description="Alert when expiry within this many days"
                      >
                        <div className="flex items-center gap-2">
                          <Input
                            id="expiringBatch"
                            type="number"
                            min={1}
                            max={90}
                            value={settings.expiringBatchAlertDays}
                            onChange={(e) =>
                              handleFieldChange('expiringBatchAlertDays', parseInt(e.target.value, 10) || 30)
                            }
                            className="w-24"
                          />
                          <span className="text-sm text-muted-foreground">days</span>
                        </div>
                      </SettingsField>
                    </CardContent>
                  </Card>
                </SettingsFormSection>
              </SettingsTabContent>
            )}

            {activeTab === 'alerts' && (
              <SettingsTabContent>
                <SettingsFormSection
                  title="Notification Preferences"
                  description="Configure how you want to receive alerts and notifications"
                >
                  <Card className="bg-muted/50">
                    <CardContent className="pt-6 space-y-4">
                      <SettingsToggleField
                        label="Email Notifications"
                        description="Receive alerts via email"
                      >
                        <ToggleSwitch
                          checked={settings.notifications.emailEnabled}
                          onCheckedChange={(checked) =>
                            handleNotificationChange('emailEnabled', checked)
                          }
                        />
                      </SettingsToggleField>

                      <SettingsToggleField
                        label="Dashboard Alerts"
                        description="Show alerts in the dashboard"
                      >
                        <ToggleSwitch
                          checked={settings.notifications.dashboardAlertsEnabled}
                          onCheckedChange={(checked) =>
                            handleNotificationChange('dashboardAlertsEnabled', checked)
                          }
                        />
                      </SettingsToggleField>

                      <SettingsToggleField
                        label="RAG Change Notifications"
                        description="Notify when RAG status changes"
                      >
                        <ToggleSwitch
                          checked={settings.notifications.ragChangeNotifications}
                          onCheckedChange={(checked) =>
                            handleNotificationChange('ragChangeNotifications', checked)
                          }
                        />
                      </SettingsToggleField>

                      <SettingsToggleField
                        label="Daily Digest"
                        description="Receive a daily summary email"
                      >
                        <ToggleSwitch
                          checked={settings.notifications.dailyDigest}
                          onCheckedChange={(checked) =>
                            handleNotificationChange('dailyDigest', checked)
                          }
                        />
                      </SettingsToggleField>

                      {settings.notifications.dailyDigest && (
                        <SettingsField
                          label="Daily Digest Time"
                          htmlFor="digestTime"
                          description="When to send the daily digest"
                        >
                          <Input
                            id="digestTime"
                            type="time"
                            value={settings.notifications.dailyDigestTime}
                            onChange={(e) =>
                              handleNotificationChange('dailyDigestTime', e.target.value)
                            }
                            className="w-32"
                          />
                        </SettingsField>
                      )}
                    </CardContent>
                  </Card>
                </SettingsFormSection>

                <SettingsFormSection
                  title="Machine Downtime"
                  description="Configure downtime alert thresholds"
                  className="mt-6"
                >
                  <SettingsField
                    label="Downtime Alert Threshold"
                    htmlFor="downtime"
                    description="Alert when machine is down for this duration"
                  >
                    <div className="flex items-center gap-2">
                      <Input
                        id="downtime"
                        type="number"
                        min={1}
                        max={168}
                        value={settings.downtimeAlertHours}
                        onChange={(e) =>
                          handleFieldChange('downtimeAlertHours', parseInt(e.target.value, 10) || 24)
                        }
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">hours</span>
                    </div>
                  </SettingsField>
                </SettingsFormSection>
              </SettingsTabContent>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
